import { useMemo } from 'react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { MoreHorizontal } from 'lucide-react';

import { ProcessStepDTO } from '@/types/api-types';

import { cn } from '@/lib/utils';

import { useIsMobile } from '@/hooks/use-mobile';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';

import {
  END_STEP_NAME,
  START_STEP_NAME,
  USER_ROLES,
} from '@/constants/global-constants';

interface AddStepsTableProps {
  steps: ProcessStepDTO[];
  onEdit: (id: ProcessStepDTO['uuid']) => void;
  onDelete: (id: ProcessStepDTO['uuid']) => void;
  isLoading?: boolean;
  isNew?: boolean;
}

const SKELETON_ROWS_COUNT = 5;

export function AddStepsTable({
  steps,
  onEdit,
  onDelete,
  isLoading,
  isNew,
}: AddStepsTableProps) {
  const isMobile = useIsMobile();

  const columns: ColumnDef<ProcessStepDTO>[] = isMobile
    ? [
        {
          accessorKey: 'id',
          header: 'Етап',
          size: 30,
          cell: ({ row }) => {
            return (
              <div className="w-full pl-4 font-semibold">
                {row.index + (isNew ? 2 : 1)}
              </div>
            );
          },
        },
        {
          accessorKey: 'status_name',
          header: 'Назва статусу',
          size: 150,
        },
        {
          id: 'actions',
          header: 'Дія',
          size: 40,
          cell: ({ row }) => {
            if (
              row.original.status_name === START_STEP_NAME ||
              row.original.status_name === END_STEP_NAME
            ) {
              return null;
            }
            return (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="h-8 w-8 p-0">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onEdit(row.original.uuid)}>
                    Редагувати
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => onDelete(row.original.uuid)}
                    className="text-destructive"
                  >
                    Видалити
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            );
          },
        },
      ]
    : [
        {
          accessorKey: 'id',
          header: 'Етап',
          size: 30,
          cell: ({ row }) => {
            return (
              <div className="w-full pl-4 font-semibold">
                {row.index + (isNew ? 2 : 1)}
              </div>
            );
          },
        },
        {
          accessorKey: 'status_name',
          header: 'Назва статусу',
          size: 150,
        },
        {
          accessorKey: 'user_type',
          header: 'Тип користувача',
          size: 150,
          cell: ({ row }) => {
            return (
              <div className="w-full">
                {
                  Object.values(USER_ROLES).find(
                    (role) => role.value === row.original.user_type,
                  )?.title
                }
              </div>
            );
          },
        },
        {
          accessorKey: 'days_until_deadline',
          header: () => {
            return <div className="w-full text-center">Днів на викон.</div>;
          },
          size: 100,
          cell: ({ row }) => {
            return (
              <div className="w-full text-center">
                {row.original.days_until_deadline === 0
                  ? ''
                  : row.original.days_until_deadline}
              </div>
            );
          },
        },
        {
          accessorKey: 'comment',
          header: 'Коментар',
          size: 350,
        },
        {
          id: 'actions',
          header: 'Дія',
          size: 40,
          cell: ({ row }) => {
            if (
              row.original.status_name === START_STEP_NAME ||
              row.original.status_name === END_STEP_NAME
            ) {
              return null;
            }
            return (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="h-8 w-8 p-0">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onEdit(row.original.uuid)}>
                    Редагувати
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => onDelete(row.original.uuid)}
                    className="text-destructive"
                  >
                    Видалити
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            );
          },
        },
      ];

  const sortedSteps = steps.sort((a, b) => {
    if (a.status_name === START_STEP_NAME) {
      return -1;
    }

    if (b.status_name === START_STEP_NAME) {
      return 1;
    }

    if (a.status_name === END_STEP_NAME) {
      return 1;
    }

    if (b.status_name === END_STEP_NAME) {
      return -1;
    }

    return 0;
  });

  const memoizedSteps = useMemo(() => {
    return sortedSteps.filter((step) => !step.is_deleted);
  }, [sortedSteps]);

  const table = useReactTable({
    data: memoizedSteps,
    columns,
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: 'onChange',
    filterFns: {
      exact: (row, columnId, value): boolean => {
        const cellValue = String(row.getValue(columnId) || '').toLowerCase();
        const filterValue = String(value).toLowerCase();
        return filterValue === '' || cellValue === filterValue;
      },
    },
  });

  const TableHeader = () => (
    <thead>
      {table.getHeaderGroups().map((headerGroup) => (
        <tr key={headerGroup.id} className="sticky top-0 z-10 bg-white">
          {headerGroup.headers.map((header) => (
            <th
              key={header.id}
              style={{ width: header.getSize() }}
              className={cn(
                'border-b border-separator pb-4 pt-4 text-left font-semibold text-primary first:pl-8 last:pr-8 last:text-right',
                ['daysUntilDeadline'].includes(header.id) && 'text-center',
              )}
            >
              {!header.isPlaceholder &&
                flexRender(header.column.columnDef.header, header.getContext())}
            </th>
          ))}
        </tr>
      ))}
    </thead>
  );

  const TableRow = ({ row }: { row: any }) => {
    // If it's a regular table row
    if (row.getVisibleCells) {
      return (
        <tr key={row.id} className="h-[70px]">
          {row.getVisibleCells().map((cell: any) => (
            <td
              key={cell.id}
              className="border-b border-separator text-primary first:pl-8 last:pr-8 last:text-right"
              style={{ width: cell.column.getSize() }}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </td>
          ))}
        </tr>
      );
    }

    // If it's our custom START/END step row
    return isMobile ? (
      <tr key={row.id} className="h-[70px]">
        <td
          className="border-b border-separator pl-8 text-primary"
          style={{ width: columns[0].size }}
        >
          <div className="w-full pl-4 font-semibold">
            {row.status_name === START_STEP_NAME ? 1 : steps.length + 2}
          </div>
        </td>
        <td
          className="border-b border-separator text-primary"
          style={{ width: columns[1].size }}
        >
          {row.status_name}
        </td>

        <td
          className="border-b border-separator pr-8 text-right text-primary"
          style={{ width: columns[2].size }}
        ></td>
      </tr>
    ) : (
      <tr key={row.id} className="h-[70px]">
        <td
          className="border-b border-separator pl-8 text-primary"
          style={{ width: columns[0].size }}
        >
          <div className="w-full pl-4 font-semibold">
            {row.status_name === START_STEP_NAME ? 1 : steps.length + 2}
          </div>
        </td>
        <td
          className="border-b border-separator text-primary"
          style={{ width: columns[1].size }}
        >
          {row.status_name}
        </td>
        <td
          className="border-b border-separator text-primary"
          style={{ width: columns[2].size }}
        >
          {
            Object.values(USER_ROLES).find(
              (role) => role.value === row.user_type,
            )?.title
          }
        </td>
        <td
          className="border-b border-separator text-center text-primary"
          style={{ width: columns[3].size }}
        >
          {row.days_until_deadline === 0 ? '' : row.days_until_deadline}
        </td>
        <td
          className="border-b border-separator text-primary"
          style={{ width: columns[4].size }}
        >
          {row.comment}
        </td>
        <td
          className="border-b border-separator pr-8 text-right text-primary"
          style={{ width: columns[5].size }}
        ></td>
      </tr>
    );
  };

  const SkeletonRows = () => (
    <>
      {Array.from({ length: SKELETON_ROWS_COUNT }).map((_, index) => (
        <tr key={`skeleton-${index}`} className="h-[70px]">
          {columns.map((_, cellIndex) => (
            <td
              key={`skeleton-cell-${cellIndex}`}
              className="border-b border-separator first:pl-8 last:pr-8"
            >
              <Skeleton className="h-4 w-[80%]" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );

  return (
    <div className="h-full rounded-md">
      <div className={`h-[calc(100%-20px)] overflow-auto`}>
        <table className="w-full border-separate border-spacing-0">
          <TableHeader />
          <tbody>
            {isLoading ? (
              <SkeletonRows />
            ) : (
              <>
                {isNew && (
                  <TableRow
                    key="add-step"
                    row={
                      {
                        id: 'add-step',
                        status_name: START_STEP_NAME,
                        user_type: USER_ROLES.EXECUTOR.value,
                        days_until_deadline: 0,
                        comment: '',
                        process_set: [],
                        uuid: '',
                        created: '',
                        modified: '',
                      } as ProcessStepDTO
                    }
                  />
                )}
                {table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id} row={row} />
                ))}
                {isNew && (
                  <TableRow
                    key="end-step"
                    row={
                      {
                        id: 'end-step',
                        status_name: END_STEP_NAME,
                        user_type: USER_ROLES.CONTROLLER.value,
                        days_until_deadline: 0,
                        comment: '',
                        process_set: [],
                        uuid: '',
                        created: '',
                        modified: '',
                      } as ProcessStepDTO
                    }
                  />
                )}
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
